import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _round from 'lodash/round';
import {
  ResultsUnitSystem,
  UnitDimension,
  UnitPackaging,
  UnitWeight,
} from 'enums/calculator';
import PatternId from 'enums/patterns';
import { LayerPatternItem, LoadDimensions } from 'interfaces/calculator';
import { AppState, PalletDataState, UnitDimensionsState } from 'interfaces/store';
import { setResultsUnitSystemAction } from 'store/actions/calculator';
import { CalculatorOptions, getPatternDimensions, shouldRotateLoad } from 'utils/calculator';
import { convertTo, convertWeightTo, roundNumber } from 'utils/numbers';
import useLayoutConfigState from './useLayoutConfigState';

const calculateTotalDimensions = (
  palletData: PalletDataState,
  load: LoadDimensions,
): LoadDimensions => {
  const {
    width: loadWidth,
    length: loadLength,
    height: loadHeight,
  } = load;
  const {
    width: palletWidth,
    length: palletLength,
    height: palletHeight,
  } = palletData;
  const width = Number(palletWidth) > Number(loadWidth) ? palletWidth : loadWidth;
  const length = Number(palletLength) > Number(loadLength) ? palletLength : loadLength;
  const height = roundNumber(Number(palletHeight) + Number(loadHeight));
  return {
    height,
    length,
    width,
  };
};

const calculateSurfaceArea = (
  palletData: PalletDataState,
  packageData: UnitDimensionsState,
  units: number,
  unitWanted: UnitDimension = UnitDimension.CM,
) => {
  let palletWidth = palletData.width;
  let palletLength = palletData.length;
  let packageWidth = packageData.width;
  let packageLength = packageData.length;
  if (palletData.unitType !== unitWanted) {
    palletWidth = convertTo(Number(palletWidth), unitWanted);
    palletLength = convertTo(Number(palletLength), unitWanted);
  }
  if (packageData.type !== unitWanted) {
    packageWidth = convertTo(Number(packageWidth), unitWanted);
    packageLength = convertTo(Number(packageLength), unitWanted);
  }
  const areaAvailable = Number(palletWidth) * Number(palletLength);
  const used = units * Number(packageWidth) * Number(packageLength);
  return roundNumber((used / areaAvailable) * 100);
};

const useResults = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    dimensions,
    palletData,
    weight,
    height,
    resultsUnitSystem,
    packagingType,
  } = useSelector(({ calculator }: AppState) => calculator);

  // eslint-disable-next-line max-len
  const dimensionsUnit = resultsUnitSystem === ResultsUnitSystem.IMPERIAL ? UnitDimension.INCH : UnitDimension.CM;
  // eslint-disable-next-line max-len
  const weightUnit = resultsUnitSystem === ResultsUnitSystem.IMPERIAL ? UnitWeight.LBS : UnitWeight.KG;

  const setResultsUnitSystem = (resultsSystem: ResultsUnitSystem) => {
    dispatch(setResultsUnitSystemAction(resultsSystem));
  };

  const {
    length: packageLength,
    type: packageDimensionType,
    width: packageWidth,
    height: packageHeight,
  } = dimensions;

  const {
    length: palletLength,
    unitType: palletUnitType,
    width: palletWidth,
  } = palletData;

  const {
    activeItem: activePattern,
    type: alignmentType,
    horizontalOverlap,
    verticalOverlap,
  } = useLayoutConfigState();

  const getLoadDimensions = (
    pattern: LayerPatternItem | null,
    numberLayers: number,
  ): LoadDimensions => {
    if (!pattern) {
      return {};
    }
    const options: CalculatorOptions = {
      packageLength,
      packageDimensionType,
      packageWidth,
      palletLength,
      palletUnitType,
      palletWidth,
      alignmentType,
      horizontalOverlap,
      verticalOverlap,
    };
    const overlapAllowed = packagingType === UnitPackaging.OVERLAP_BAG;
    const load = getPatternDimensions(pattern, options, overlapAllowed);

    load.height = palletUnitType !== packageDimensionType
      ? numberLayers * convertTo(Number(packageHeight), palletUnitType)
      : numberLayers * Number(packageHeight);

    if (palletUnitType !== dimensionsUnit) {
      load.height = convertTo(Number(load.height), dimensionsUnit);
      load.width = convertTo(Number(load.width), dimensionsUnit);
      load.length = convertTo(Number(load.length), dimensionsUnit);
    }

    let width;
    let length;

    if (shouldRotateLoad(pattern, options)) {
      width = roundNumber(load.length);
      length = roundNumber(load.width);
    } else {
      width = roundNumber(load.width);
      length = roundNumber(load.length);
    }

    return {
      height: load.height,
      width,
      length,
    };
  };

  const getPalletConverted = () => {
    const converted = palletData;
    if (palletUnitType !== dimensionsUnit) {
      converted.height = convertTo(Number(palletData.height), dimensionsUnit);
      converted.width = convertTo(Number(palletData.width), dimensionsUnit);
      converted.length = convertTo(Number(palletData.length), dimensionsUnit);
      converted.unitType = dimensionsUnit;
    }

    converted.height = roundNumber(converted.height);
    converted.width = roundNumber(converted.width);
    converted.length = roundNumber(converted.length);
    return converted;
  };

  // Convert pallet dimensions.
  const palletDimensions = getPalletConverted();

  const getPackageConverted = () => {
    const packConverted = {
      length: packageLength,
      type: packageDimensionType,
      width: packageWidth,
      height: packageHeight,
    };

    if (packageDimensionType !== dimensionsUnit) {
      packConverted.height = convertTo(Number(packageHeight), dimensionsUnit);
      packConverted.width = convertTo(Number(packageWidth), dimensionsUnit);
      packConverted.length = convertTo(Number(packageLength), dimensionsUnit);
      packConverted.type = dimensionsUnit;
    }

    dimensions.height = roundNumber(dimensions.height);
    dimensions.width = roundNumber(dimensions.width);
    dimensions.length = roundNumber(dimensions.length);
    return packConverted;
  };

  const packDimensions = getPackageConverted();

  // Get number of layers.
  // eslint-disable-next-line max-len
  const numberLayers = height.layers ?? 1;
  const totalLayers = t('numberOfLayers', { count: numberLayers });

  // Calculate load dimensions.
  const loadDimensions = getLoadDimensions(activePattern, numberLayers);
  const {
    width: loadWidth,
    length: loadLength,
    height: loadHeight,
    numberProducts,
  } = loadDimensions;

  // Get units per layer.
  const unitsPerLayerValueTemp = numberProducts || activePattern?.value;
  const unitsPerLayerValue = activePattern?.label === PatternId.GRID
    ? Number(activePattern.cols) * Number(activePattern.rows)
    : unitsPerLayerValueTemp;
  const unitsPerLayer = `${unitsPerLayerValue} units`;

  // Calculate total dimensions.
  const totalDimensions = calculateTotalDimensions(palletDimensions, loadDimensions);
  const {
    height: totalHeight,
  } = totalDimensions;
  const heightString = `${totalHeight} ${dimensionsUnit.toLowerCase()}`;

  // Calculate total number of packages.
  const packages = activePattern ? Number(unitsPerLayerValue) * numberLayers : 0;
  const packagesString = t('package', { count: packages });

  // Calculate total weight.
  let totalWeight = roundNumber(Number(weight.value) * packages);
  if (weight.type !== weightUnit) {
    totalWeight = convertWeightTo(totalWeight, weightUnit);
  }
  const totalWeightString = `${totalWeight} ${weightUnit.toLowerCase()}`;

  /* Calculate overhangs. */
  const widthDifference = _round(Number(loadWidth) - Number(palletDimensions.width), 2);
  const overhangUnit = dimensionsUnit === UnitDimension.INCH ? 'in' : 'cm';
  const widthOverhang = widthDifference > 0 ? `${widthDifference / 2} ${overhangUnit}` : '0';
  const lengthDifference = _round(Number(loadLength) - Number(palletDimensions.length), 2);
  const lengthOverhang = lengthDifference > 0 ? `${lengthDifference / 2} ${overhangUnit}` : '0';
  const overhang = `W: ${widthOverhang} | L: ${lengthOverhang}`;

  // Calculate load volume.
  const volume = roundNumber(Number(loadWidth) * Number(loadLength) * Number(loadHeight));

  // Calculate surface usage.
  const surfaceUsage = activePattern ? `${calculateSurfaceArea(
    palletData,
    dimensions,
    Number(unitsPerLayerValue),
    dimensionsUnit,
  )}%` : '0';

  return {
    packDimensions,
    dimensionsUnit,
    heightString,
    overhang,
    packagesString,
    resultsUnitSystem,
    surfaceUsage,
    totalLayers,
    totalWeightString,
    unitsPerLayer,
    volume,
    palletDimensions,
    totalDimensions,
    loadDimensions,
    widthDifference,
    lengthDifference,

    setResultsUnitSystem,
  };
};

export default useResults;
